import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NotificationFacade } from '@trp/community/notification/client-data-access';
import { ProgramVerificationApiClient } from '@trp/community/program-verification/api-client';
import { CollapsibleMenuService } from '@trp/community/shared/data-access-collapsible-menu';
import { UserService } from '@trp/community/user/feature-user';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { USAStateEnum } from '@trp/community/auth/interfaces';
import { FooterService } from '@trp/community/ui/base-layout';

@Component({
  selector: 'community-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild('footerContainer', { read: ViewContainerRef, static: true })
  footerContainer!: ViewContainerRef;

  public stateName$: Observable<USAStateEnum | undefined>;
  constructor(
    public notificationFacade: NotificationFacade,
    public user: UserService,
    public menu: CollapsibleMenuService,
    private title: Title,
    private translateService: TranslateService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    public footerService: FooterService,
    private programVerificationApiClient: ProgramVerificationApiClient
  ) {
    this.translateService
      .stream('toolTitle')
      .pipe(
        take(1),
        tap((text) => this.title.setTitle(text))
      )
      .subscribe();
    this.iconRegistry.addSvgIconResolver((name, namespace) => {
      if (namespace === 'custom-solid') {
        return this.sanitizer.bypassSecurityTrustResourceUrl(
          window.location.origin +
            '/assets/icons/CommunityTool_MicroIcons_Solid_v2/' +
            name +
            '_Solid.svg'
        );
      }
      if (namespace === 'custom-light') {
        return this.sanitizer.bypassSecurityTrustResourceUrl(
          window.location.origin +
            '/assets/icons/CommunityTool_MicroIcons_Light_v2/' +
            name +
            '_Light.svg'
        );
      }

      if (namespace === 'global') {
        return this.sanitizer.bypassSecurityTrustResourceUrl(
          window.location.origin + '/assets/icons/' + name + '.svg'
        );
      }

      return null;
    });
  }

  ngOnInit(): void {
    this.stateName$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.findStateName(this.route))
    );

    // On load, ask the backend to surface any verification that has expired but
    // the user has not been notified of yet, then refresh notifications so the
    // banner shows up immediately.
    this.user.isSignedIn$
      .pipe(
        filter((isSignedIn) => isSignedIn),
        take(1),
        switchMap(() =>
          this.programVerificationApiClient.notifyExpiredVerifications()
        )
      )
      .subscribe(() => this.notificationFacade.fetchNotifications());

    this.footerService.footerComponent$.subscribe((componentType) => {
      if (componentType) {
        this.footerContainer.createComponent(componentType);
      } else {
        this.footerContainer.clear();
      }
    });
  }

  private findStateName(route: ActivatedRoute): USAStateEnum | undefined {
    if (route.snapshot.data && route.snapshot.data['stateName']) {
      return route.snapshot.data['stateName'];
    }
    if (route.firstChild) {
      return this.findStateName(route.firstChild);
    }
    return undefined;
  }
}
